import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/AuthContext';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Field';
import { Banner, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { FileUpload } from '@/components/ui/FileUpload';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  STATUS_LABEL,
  assessmentApi,
  type AnswerPayload,
  type AssessmentDetail,
  type FormIndicator,
  type ScoreResult,
} from './api';
import { useAutosave, type SaveState } from './useAutosave';

/** Kunci jawaban: satu indikator bisa dijawab per orang di bagian matriks. */
const keyOf = (indicatorId: string, individualId: string | null) =>
  `${indicatorId}::${individualId ?? ''}`;

/** L-08 — Form penilaian dinamis dari instrumen, tersimpan otomatis. */
export function AssessmentFormPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [answers, setAnswers] = useState<Map<string, AnswerPayload>>(new Map());
  const [score, setScore] = useState<ScoreResult | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmingReopen, setConfirmingReopen] = useState(false);
  const [isEditing, setIsEditing] = useState(searchParams.get('edit') === 'true');
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['assessment', id],
    queryFn: () => assessmentApi.get(id),
  });

  // Jawaban tersimpan dimuat sekali ke state lokal. Sesudah itu layar yang
  // memegang kebenaran sementara, supaya mengetuk skor terasa seketika dan
  // tidak menunggu perjalanan ke server.
  if (data && !loaded) {
    setAnswers(
      new Map(
        data.answers.map((a) => [
          keyOf(a.indicatorId, a.individualId),
          {
            indicatorId: a.indicatorId,
            individualId: a.individualId,
            score: a.score,
            rawInput: a.rawInput,
          },
        ]),
      ),
    );
    setLoaded(true);
  }

  const autosave = useAutosave<AnswerPayload[]>(async (payload) => {
    const result = await assessmentApi.saveAnswers(id, payload);
    setScore(result);
    return result;
  });

  const isLockedByPeriod = data?.period.status === 'locked' && user?.role !== 'super_admin';
  const isNaturallyEditable = data?.status === 'draft' || data?.status === 'returned';
  const readOnly = isLockedByPeriod || (!isNaturallyEditable && !isEditing);

  function setAnswer(indicatorId: string, individualId: string | null, patch: Partial<AnswerPayload>) {
    if (readOnly) return;
    const next = new Map(answers);
    const key = keyOf(indicatorId, individualId);
    const current = next.get(key) ?? { indicatorId, individualId, score: null, rawInput: null };
    next.set(key, { ...current, ...patch });
    setAnswers(next);
    autosave.schedule([...next.values()]);
  }

  const saveAndExit = async () => {
    await autosave.flush();
    queryClient.invalidateQueries({ queryKey: ['assessment', id] });
    queryClient.invalidateQueries({ queryKey: ['assessments'] });
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
    setIsEditing(false);
    searchParams.delete('edit');
    setSearchParams(searchParams);
  };

  const reopen = useMutation({
    mutationFn: () => assessmentApi.reopen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', id] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      setConfirmingReopen(false);
      setIsEditing(true);
    },
    onError: (err) => {
      setConfirmingReopen(false);
      setError(err instanceof ApiError ? err.message : 'Gagal membuka kunci penilaian.');
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      // Perubahan yang masih tertunda dikirim DULU. Tanpa ini, ketukan
      // terakhir bisa hilang karena submit mendahului autosave.
      await autosave.flush();
      return assessmentApi.submit(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', id] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      setConfirming(false);
      setError(null);
    },
    onError: (err) => {
      setConfirming(false);
      setError(err instanceof ApiError ? err.message : 'Gagal mengirim penilaian.');
    },
  });

  const allIndicators = useMemo(
    () => data?.sections.flatMap((s) => s.dimensions.flatMap((d) => d.indicators)) ?? [],
    [data],
  );

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (loadError) return <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const hasMatrix = data.sections.some((s) => s.entryMode === 'matrix');
  const answered = [...answers.values()].filter(
    (a) => a.score !== null || a.rawInput !== null,
  ).length;
  const expected = hasMatrix ? null : allIndicators.length;
  const isComplete = score?.isComplete ?? (expected !== null && answered >= expected);

  const backLink =
    data.subjectType === 'individual'
      ? '/pengukuran/individu'
      : data.instrument.scope === 'composite'
        ? '/pengukuran/komposit'
        : '/pengukuran/tbm';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={backLink} className="text-sm text-ink-secondary hover:text-primary">
          ← {data.subjectType === 'individual' ? 'Daftar Kompetensi PELITA SLI' : 'Daftar Penilaian'}
        </Link>
        {user?.role === 'super_admin' && (data.status === 'submitted' || data.status === 'verified') && (
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  Ubah Isian Penilaian
                </Button>
                <Button variant="ghost" onClick={() => setConfirmingReopen(true)}>
                  Buka Kunci ke Draf
                </Button>
              </>
            ) : (
              <Button onClick={saveAndExit}>
                Selesai Sunting
              </Button>
            )}
          </div>
        )}
      </div>

      {isEditing && (
        <Banner tone="info">
          <strong>Mode Pengubahan Aktif:</strong> Anda sedang menyunting penilaian berstatus{' '}
          <em>{STATUS_LABEL[data.status]}</em>. Perubahan jawaban akan langsung disimpan dan memperbarui skor indeks.
        </Banner>
      )}

      {error && <Banner tone="danger">{error}</Banner>}

      {data.status === 'returned' && data.reviewerNote && (
        <Banner tone="danger">
          Dikembalikan oleh verifikator: <strong>{data.reviewerNote}</strong>
        </Banner>
      )}

      {data.period.status === 'locked' && (
        <Banner tone="info">
          Periode <strong>{data.period.name}</strong> sudah dikunci — penilaian ini hanya bisa
          dibaca.
        </Banner>
      )}

      <ScoreHeader data={data} score={score} saveState={autosave.state} readOnly={readOnly} />

      {data.sections.map((section) => (
        <SectionForm
          key={section.id}
          assessmentId={id}
          section={section}
          data={data}
          answers={answers}
          readOnly={readOnly}
          onChange={setAnswer}
        />
      ))}

      {data.status === 'submitted' && <ReviewPanel id={id} onError={setError} />}

      {!readOnly && isNaturallyEditable && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[15px] text-ink-secondary">
              {expected === null
                ? `${answered} jawaban terisi`
                : `${score?.answeredCount ?? answered} dari ${expected} indikator terisi`}
              {!isComplete && ' — kategori muncul setelah semuanya terisi.'}
            </p>
            <Button onClick={() => setConfirming(true)} disabled={!isComplete}>
              Kirim Penilaian
            </Button>
          </div>
        </Card>
      )}

      {isEditing && !isNaturallyEditable && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[15px] text-ink-secondary">
              Perubahan disimpan otomatis. Skor terhitung:{' '}
              <strong>
                {score
                  ? Math.round(score.index * 100) / 100
                  : Math.round((data.totalScore ?? 0) * 100) / 100}
              </strong>
              {(score?.band || data.band) && ` (${score?.band ?? data.band})`}
            </p>
            <Button onClick={saveAndExit}>
              Selesai Sunting
            </Button>
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={confirming}
        title="Kirim penilaian?"
        confirmLabel="Ya, kirim"
        tone="primary"
        loading={submit.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => submit.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5">
          <span className="tabular text-2xl font-semibold text-ink">
            {score ? Math.round(score.index * 100) / 100 : '—'}
          </span>
          {score?.band && <span className="ml-2 font-medium text-primary">{score.band}</span>}
        </p>
        <p>
          Setelah dikirim, jawaban <strong>tidak bisa diubah</strong> dan indeksnya dibekukan —
          menyunting instrumen di kemudian hari tidak akan menggesernya. Verifikator akan meninjau,
          dan bisa mengembalikannya kalau ada yang perlu diperbaiki.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmingReopen}
        title="Kembalikan status penilaian ke Draf?"
        confirmLabel="Ya, buka kunci"
        tone="primary"
        loading={reopen.isPending}
        onCancel={() => setConfirmingReopen(false)}
        onConfirm={() => reopen.mutate()}
      >
        <p>
          Status penilaian akan dikembalikan menjadi <strong>Draf</strong> sehingga penilai dapat
          mengisi ulang secara mandiri.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/**
 * Panel verifikasi, muncul hanya untuk penilaian berstatus TERKIRIM.
 *
 * Server tetap yang memutuskan boleh atau tidak — penilai tidak bisa
 * memverifikasi pekerjaannya sendiri, dan tombol di sini tidak
 * menggantikan pemeriksaan itu, hanya menampilkannya lebih awal.
 */
function ReviewPanel({
  id,
  onError,
}: {
  id: string;
  onError: (message: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState<'verify' | 'return' | null>(null);

  const review = useMutation({
    mutationFn: (action: 'verify' | 'return') => assessmentApi.review(id, action, note || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment', id] });
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      setConfirming(null);
      onError(null);
    },
    onError: (err) => {
      setConfirming(null);
      onError(err instanceof ApiError ? err.message : 'Gagal memproses peninjauan.');
    },
  });

  return (
    <>
      <Card className="border border-primary/30">
        <h2 className="text-[17px] font-semibold text-ink">Peninjauan</h2>
        <p className="mt-0.5 text-[13px] text-ink-secondary">
          Penilaian ini menunggu diverifikasi. Kembalikan bila ada yang perlu diperbaiki — jawaban
          akan bisa disunting lagi oleh penilainya.
        </p>

        <div className="mt-4">
          <Input
            label="Catatan verifikator"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            hint="Wajib diisi kalau dikembalikan, agar penilainya tahu apa yang harus diperbaiki."
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => setConfirming('verify')}>Verifikasi</Button>
          <Button variant="secondary" onClick={() => setConfirming('return')} disabled={!note.trim()}>
            Kembalikan
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === 'verify' ? 'Verifikasi penilaian?' : 'Kembalikan penilaian?'}
        confirmLabel={confirming === 'verify' ? 'Ya, verifikasi' : 'Ya, kembalikan'}
        tone={confirming === 'verify' ? 'primary' : 'danger'}
        loading={review.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => review.mutate(confirming!)}
      >
        {confirming === 'verify' ? (
          <p>
            Penilaian dikunci permanen dan indeksnya menjadi angka resmi periode ini. Setelah
            diverifikasi, jawabannya tidak bisa diubah oleh siapa pun.
          </p>
        ) : (
          <p>
            Penilai bisa menyunting dan mengirim ulang. Catatan Anda akan tampil di atas formnya:
            <strong> “{note}”</strong>
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

function ScoreHeader({
  data,
  score,
  saveState,
  readOnly,
}: {
  data: AssessmentDetail;
  score: ScoreResult | null;
  saveState: SaveState;
  readOnly: boolean;
}) {
  // Selama mengisi, indeks diambil dari perhitungan terakhir server; setelah
  // dikirim, dari angka yang sudah dibekukan di baris penilaian.
  const shown = data.totalScore ?? score?.index ?? null;
  const band = data.band ?? score?.band ?? null;

  return (
    <Card className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'rounded-full px-2.5 py-1 text-[13px] font-medium',
                data.status === 'verified'
                  ? 'bg-success-soft text-success'
                  : data.status === 'submitted'
                    ? 'bg-primary-soft text-primary'
                    : data.status === 'returned'
                      ? 'bg-danger-soft text-danger'
                      : 'bg-warn-soft text-[#9a6400]',
              )}
            >
              {STATUS_LABEL[data.status]}
            </span>
            {data.programYear && (
              <span className="rounded-full bg-app px-2.5 py-1 text-[13px] text-ink-secondary">
                Tahun Program {data.programYear}
              </span>
            )}
            <span className="text-[13px] text-ink-secondary">{data.period.name}</span>
          </div>

          <h1 className="mt-2 text-2xl font-semibold text-ink">
            {data.subject?.name ?? 'Subjek tidak ditemukan'}
          </h1>
          <p className="mt-0.5 text-[13px] text-ink-secondary">
            {data.instrument.name} · v{data.instrument.version}
          </p>
        </div>

        <div className="text-right">
          <p className="text-[13px] text-ink-secondary">Indeks</p>
          <p className="tabular text-4xl font-semibold text-ink">
            {shown === null ? '—' : Math.round(shown * 100) / 100}
          </p>
          <p className="text-sm font-medium text-primary">{band ?? 'belum lengkap'}</p>
          {!readOnly && <SaveBadge state={saveState} />}
        </div>
      </div>
    </Card>
  );
}

function SaveBadge({ state }: { state: SaveState }) {
  const map: Record<SaveState, { text: string; cls: string }> = {
    idle: { text: 'Perubahan tersimpan otomatis', cls: 'text-ink-muted' },
    pending: { text: 'Menunggu simpan…', cls: 'text-ink-secondary' },
    saving: { text: 'Menyimpan…', cls: 'text-ink-secondary' },
    saved: { text: '✓ Tersimpan', cls: 'text-success' },
    error: { text: '⚠ Gagal menyimpan — coba ubah lagi', cls: 'text-danger' },
  };
  const s = map[state];
  return <p className={cn('mt-1 text-[13px]', s.cls)}>{s.text}</p>;
}

function SectionForm({
  assessmentId,
  section,
  data,
  answers,
  readOnly,
  onChange,
}: {
  assessmentId: string;
  section: AssessmentDetail['sections'][number];
  data: AssessmentDetail;
  answers: Map<string, AnswerPayload>;
  readOnly: boolean;
  onChange: (indicatorId: string, individualId: string | null, patch: Partial<AnswerPayload>) => void;
}) {
  const multi = data.sections.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {multi && (
        <Card className="bg-primary-soft/50">
          <h2 className="text-[17px] font-semibold text-ink">{section.name}</h2>
          <p className="mt-0.5 text-[13px] text-ink-secondary">
            {Math.round(section.weightInFinal * 100)}% dari nilai akhir
            {section.entryMode === 'matrix' && ' · diisi untuk setiap PELITA SLI'}
          </p>
        </Card>
      )}

      {section.dimensions.map((dimension) => (
        <Card key={dimension.id} padded={false} className="min-w-0">
          <div className="border-b border-border px-5 py-4">
            <h3 className="font-semibold text-ink">
              <span className="tabular text-ink-muted">{dimension.code}</span> {dimension.name}
            </h3>
          </div>

          <ul className="divide-y divide-[color:var(--color-border)]">
            {dimension.indicators.map((indicator) =>
              section.entryMode === 'matrix' ? (
                <MatrixRow
                  key={indicator.id}
                  indicator={indicator}
                  members={data.members}
                  answers={answers}
                  readOnly={readOnly}
                  scaleLabels={data.instrument.scaleLabels}
                  onChange={onChange}
                />
              ) : (
                <IndicatorRow
                  key={indicator.id}
                  assessmentId={assessmentId}
                  indicator={indicator}
                  answer={answers.get(keyOf(indicator.id, null))}
                  readOnly={readOnly}
                  evidenceLabel={data.instrument.evidenceLabel ?? 'Bukti'}
                  scaleLabels={data.instrument.scaleLabels}
                  onChange={(patch) => onChange(indicator.id, null, patch)}
                />
              ),
            )}
          </ul>
        </Card>
      ))}
    </div>
  );
}

function IndicatorRow({
  assessmentId,
  indicator,
  answer,
  readOnly,
  evidenceLabel,
  scaleLabels,
  onChange,
}: {
  assessmentId: string;
  indicator: FormIndicator;
  answer: AnswerPayload | undefined;
  readOnly: boolean;
  evidenceLabel: string;
  scaleLabels: { score: number; label: string }[];
  onChange: (patch: Partial<AnswerPayload>) => void;
}) {
  return (
    <li className="px-5 py-4">
      <div className="flex items-start gap-3">
        <span className="tabular mt-0.5 w-6 shrink-0 text-sm text-ink-muted">
          {indicator.number}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink">{indicator.name}</p>
          {indicator.description && (
            <p className="mt-0.5 text-[13px] text-ink-secondary">{indicator.description}</p>
          )}
        </div>
        <span className="tabular shrink-0 text-sm text-ink-secondary">bobot {indicator.weight}</span>
      </div>

      <div className="mt-3.5 pl-9">
        {indicator.inputType === 'percentage_auto' ? (
          <div className="sm:max-w-xs">
            <Input
              label="Persentase capaian"
              type="number"
              inputMode="decimal"
              min={0}
              max={100}
              disabled={readOnly}
              value={answer?.rawInput ?? ''}
              onChange={(e) =>
                onChange({ rawInput: e.target.value === '' ? null : Number(e.target.value) })
              }
              hint="Skornya dihitung otomatis dari ambang indikator ini."
            />
          </div>
        ) : (
          <ScaleSelector
            criteria={indicator.criteria}
            scaleLabels={scaleLabels}
            value={answer?.score ?? null}
            readOnly={readOnly}
            onSelect={(score) => onChange({ score })}
          />
        )}

        <div className="mt-3.5 border-t border-border pt-3">
          {/* Daftar MoV dari instrumen. Ini yang memberi tahu asesor bukti apa
              yang harus dicari di lapangan — tanpa daftarnya, judul bagian ini
              menyebut "cara memperoleh nilai" tanpa pernah menyebut caranya. */}
          {indicator.mov.length > 0 && (
            <div className="mb-3">
              <p className="mb-1.5 text-[13px] font-medium text-ink-secondary">{evidenceLabel}</p>
              <ul className="flex flex-col gap-1">
                {indicator.mov.map((m) => (
                  <li key={m.label} className="flex gap-2 text-[14px] text-ink">
                    <span className="text-ink-muted" aria-hidden>
                      •
                    </span>
                    <span>
                      {m.label}
                      {m.requirement === 'required' && (
                        <span className="ml-1.5 text-[12px] font-medium text-danger">wajib</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Lampiran ditautkan ke pasangan penilaian+indikator, bukan ke baris
              jawaban — jawabannya bisa dihapus dan dibuat ulang saat skornya
              dikosongkan, dan buktinya tidak boleh ikut hilang karena itu. */}
          <FileUpload
            entityType="assessment_indicator"
            entityId={`${assessmentId}:${indicator.id}`}
            // Judul kotak unggah dibedakan dari judul daftar MoV di atasnya:
            // keduanya memakai istilah instrumen akan terbaca sebagai satu
            // bagian yang sama tercetak dua kali.
            label={indicator.mov.length > 0 ? 'Unggah bukti' : evidenceLabel}
            readOnly={readOnly}
            hint="JPG, PNG, WebP, atau PDF · maksimal 5 MB"
          />
        </div>
      </div>
    </li>
  );
}

/**
 * Pemilih skala 1–4 yang menampilkan KRITERIANYA, bukan hanya angkanya.
 *
 * Assessor memilih berdasarkan kalimat yang paling menggambarkan keadaan, dan
 * angka mengikuti — bukan sebaliknya. Menampilkan angka saja memaksa mereka
 * bolak-balik ke dokumen instrumen, dan di lapangan itu berarti menebak.
 */
function ScaleSelector({
  criteria,
  scaleLabels,
  value,
  readOnly,
  onSelect,
}: {
  criteria: { score: number; description: string }[];
  scaleLabels: { score: number; label: string }[];
  value: number | null;
  readOnly: boolean;
  onSelect: (score: number | null) => void;
}) {
  const scores = criteria.length > 0 ? criteria.map((c) => c.score) : [1, 2, 3, 4];

  return (
    <RadioGroup
      label="Pilih kriteria yang paling sesuai"
      className="grid gap-2 sm:grid-cols-2"
      options={scores}
      value={value}
      disabled={readOnly}
      getKey={(score) => score}
      // Mengetuk pilihan yang sama membatalkannya — satu-satunya cara
      // mengosongkan jawaban yang terlanjur diisi.
      onChange={onSelect}
      getLabel={(score) =>
        [`Skor ${score}`, scaleLabels.find((s) => s.score === score)?.label]
          .filter(Boolean)
          .join(' — ')
      }
      optionClassName={(_, { selected }) =>
        cn(
          'rounded-[var(--radius-input)] border p-3 text-left transition-colors',
          'min-h-11 disabled:cursor-not-allowed disabled:opacity-60',
          selected
            ? 'border-primary bg-primary-soft'
            : 'border-border bg-surface hover:border-primary',
        )
      }
    >
      {(score, { selected }) => {
        const label = scaleLabels.find((s) => s.score === score)?.label;
        const description = criteria.find((c) => c.score === score)?.description;

        return (
          <>
            <span
              className={cn(
                'flex items-center gap-2 text-[13px] font-semibold',
                selected ? 'text-primary' : 'text-ink-secondary',
              )}
            >
              <span
                className={cn(
                  'grid size-5 shrink-0 place-items-center rounded-full text-[12px]',
                  selected ? 'bg-primary text-white' : 'bg-app text-ink-secondary',
                )}
              >
                {score}
              </span>
              {label}
            </span>
            {description && <span className="mt-1 block text-[14px] text-ink">{description}</span>}
          </>
        );
      }}
    </RadioGroup>
  );
}

/**
 * Baris matriks: satu indikator dijawab sekali untuk TIAP PELITA.
 *
 * Di layar sempit ini ditumpuk per orang, bukan digulir sebagai grid lebar —
 * tabel yang harus digeser ke samping praktis tidak terpakai di HP, padahal
 * PRD menempatkan HP sebagai perangkat utama.
 */
function MatrixRow({
  indicator,
  members,
  answers,
  readOnly,
  scaleLabels,
  onChange,
}: {
  indicator: FormIndicator;
  members: { id: string; code: string; name: string }[];
  answers: Map<string, AnswerPayload>;
  readOnly: boolean;
  scaleLabels: { score: number; label: string }[];
  onChange: (indicatorId: string, individualId: string | null, patch: Partial<AnswerPayload>) => void;
}) {
  return (
    <li className="px-5 py-4">
      <div className="flex items-start gap-3">
        <span className="tabular mt-0.5 w-6 shrink-0 text-sm text-ink-muted">
          {indicator.number}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink">{indicator.name}</p>
          {indicator.description && (
            <p className="mt-0.5 text-[13px] text-ink-secondary">{indicator.description}</p>
          )}
        </div>
        <span className="tabular shrink-0 text-sm text-ink-secondary">bobot {indicator.weight}</span>
      </div>

      {members.length === 0 ? (
        <p className="mt-3 pl-9 text-[13px] text-ink-muted">
          Belum ada PELITA SLI di TBM ini, jadi bagian ini belum bisa diisi.
        </p>
      ) : (
        <div className="mt-3.5 flex flex-col gap-3 pl-9">
          {members.map((member) => {
            const answer = answers.get(keyOf(indicator.id, member.id));
            return (
              <div key={member.id} className="rounded-[var(--radius-input)] bg-app p-3">
                <p className="mb-2 text-[13px] font-medium text-ink">
                  <span className="tabular text-ink-muted">{member.code}</span> {member.name}
                </p>
                <RadioGroup
                  // Nama kelompok menyebut ORANGNYA: di mode matriks ada satu
                  // kelompok per PELITA pada indikator yang sama, dan tanpa ini
                  // pembaca layar mengumumkan empat kelompok yang identik.
                  label={`Skor ${member.name} untuk indikator ${indicator.number}`}
                  className="flex flex-wrap gap-1.5"
                  options={[1, 2, 3, 4]}
                  value={answer?.score ?? null}
                  disabled={readOnly}
                  getKey={(score) => score}
                  onChange={(score) => onChange(indicator.id, member.id, { score })}
                  getLabel={(score) =>
                    indicator.criteria.find((c) => c.score === score)?.description ??
                    scaleLabels.find((s) => s.score === score)?.label ??
                    `Skor ${score}`
                  }
                  optionClassName={(_, { selected }) =>
                    cn(
                      'tabular min-h-11 min-w-11 rounded-lg border text-sm font-medium',
                      'disabled:cursor-not-allowed disabled:opacity-60',
                      selected
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-surface text-ink hover:border-primary',
                    )
                  }
                >
                  {(score) => score}
                </RadioGroup>
              </div>
            );
          })}
        </div>
      )}
    </li>
  );
}
